import { useNavigate } from 'react-router-dom';
import { Check, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

function PricingPage() {
  const navigate = useNavigate();

  const pricingPlans = [
    {
      name: 'Basic',
      price: '₹999',
      period: '/month',
      description: 'Perfect for small homestays starting out',
      features: [
        'List 1 property',
        'Basic profile visibility',
        'Receive guest referrals',
        'Access to partner network',
        'Monthly performance reports',
        'Email support'
      ],
      popular: false,
      badge: 'Starter Plan'
    },
    {
      name: 'Pro',
      price: '₹2,499',
      period: '/month',
      description: 'Most popular for growing resorts',
      features: [
        'List up to 3 properties',
        'Premium profile placement',
        'Priority guest referrals',
        'Featured in newsletters',
        'Advanced analytics dashboard',
        'Dedicated account manager',
        '24/7 priority support',
        'Marketing materials support'
      ],
      popular: true,
      badge: 'Most Popular'
    },
    {
      name: 'Premium',
      price: '₹4,999',
      period: '/month',
      description: 'For established resort groups',
      features: [
        'Unlimited properties',
        'Top-tier profile visibility',
        'Maximum referral priority',
        'Off-Season Collaboration Network 🌟',
        'Custom landing pages',
        'API integration',
        'White-label solutions',
        'Quarterly strategy sessions',
        'Custom marketing campaigns',
        'Exclusive partnership events'
      ],
      popular: false,
      badge: 'Enterprise'
    }
  ];

  const faqs = [
    {
      question: 'Can I switch plans later?',
      answer: 'Yes, you can upgrade or downgrade your plan anytime. Changes take effect from the next billing cycle with pro-rated adjustments.'
    },
    {
      question: 'Is there a setup fee?',
      answer: 'No setup fees or hidden charges. You only pay the monthly subscription for your chosen plan.'
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit cards, debit cards, net banking, and UPI payments. Invoicing available for annual subscriptions.'
    },
    {
      question: 'Can I cancel anytime?',
      answer: 'Yes, no long-term contracts. Cancel anytime with 30 days notice period. No cancellation fees.'
    },
    {
      question: 'Do you take commission on bookings?',
      answer: 'No! Unlike OTAs, we charge zero commission on bookings. You keep 100% of your revenue. Just pay the flat monthly subscription.'
    },
    {
      question: 'Is there a free trial?',
      answer: 'Yes! All new partners get a 30-day free trial to experience the platform. No credit card required to start.'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-emerald-50 to-white" data-testid="pricing-hero">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">Simple, Transparent Pricing</h1>
          <p className="text-xl text-gray-600 mb-6">Choose the plan that fits your business. No hidden fees. Cancel anytime.</p>
          <Badge className="bg-emerald-600 text-white px-6 py-2 text-base">
            🎉 30-Day Free Trial for New Partners!
          </Badge>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="pricing-plans">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {pricingPlans.map((plan) => (
              <Card 
                key={plan.name} 
                className={`relative border-2 transition-all ${
                  plan.popular 
                    ? 'border-emerald-500 shadow-2xl scale-105' 
                    : 'border-gray-200 hover:border-emerald-300 hover:shadow-xl'
                }`}
                data-testid={`plan-${plan.name.toLowerCase()}`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-emerald-600 text-white px-6 py-1.5 text-sm">{plan.badge}</Badge>
                  </div>
                )}
                {!plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge variant="outline" className="bg-white px-4 py-1 text-sm">{plan.badge}</Badge>
                  </div>
                )}
                <CardHeader className="text-center pb-8 pt-10">
                  <CardTitle className="text-3xl mb-2">{plan.name}</CardTitle>
                  <div className="mb-4">
                    <span className="text-5xl font-bold text-gray-900">{plan.price}</span>
                    <span className="text-gray-600 text-lg">{plan.period}</span>
                  </div>
                  <CardDescription className="text-base">{plan.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <Check className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    className={`w-full py-6 text-lg ${
                      plan.popular 
                        ? 'bg-emerald-600 hover:bg-emerald-700' 
                        : 'bg-gray-900 hover:bg-gray-800'
                    }`}
                    onClick={() => navigate('/join')}
                    data-testid={`select-${plan.name.toLowerCase()}`}
                  >
                    Join Today
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Comparison Note */}
          <Card className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white">
            <CardContent className="py-8">
              <div className="text-center">
                <h3 className="text-2xl font-bold mb-4">Compare with Traditional OTAs</h3>
                <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                  <div className="bg-white/10 rounded-lg p-6">
                    <div className="text-sm text-emerald-100 mb-2">OTA Commission Model</div>
                    <div className="text-4xl font-bold mb-2">15-30%</div>
                    <div className="text-sm text-emerald-100">Per booking commission</div>
                    <div className="mt-4 text-sm">
                      On ₹10,00,000 bookings = <span className="font-bold text-xl">₹1,50,000-3,00,000</span> in fees
                    </div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-6">
                    <div className="text-sm text-emerald-100 mb-2">Travelodges Model</div>
                    <div className="text-4xl font-bold mb-2">₹999-₹4,999</div>
                    <div className="text-sm text-emerald-100">Flat monthly fee</div>
                    <div className="mt-4 text-sm">
                      On ₹10,00,000 bookings = <span className="font-bold text-xl">₹12,000-60,000</span> annually
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="faq-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600">Everything you need to know about pricing</p>
          </div>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <Card key={index} data-testid={`faq-${index}`}>
                <CardContent className="pt-6">
                  <h4 className="font-semibold text-lg text-gray-900 mb-2">{faq.question}</h4>
                  <p className="text-gray-600">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-emerald-600 to-emerald-800" data-testid="pricing-cta">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-emerald-100 mb-10">
            Join 500+ resorts growing their business. Start your free 30-day trial today.
          </p>
          <Button 
            size="lg" 
            className="bg-white text-emerald-600 hover:bg-gray-100 text-lg px-10 py-6"
            onClick={() => navigate('/join')}
          >
            Start Free Trial
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <p className="text-emerald-100 mt-6">
            <Check className="inline h-4 w-4 mr-1" />
            No credit card required • <Check className="inline h-4 w-4 mx-1" /> Cancel anytime
          </p>
        </div>
      </section>
    </div>
  );
}

export default PricingPage;